import { useState } from "react";
import { useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { ReasonDialog } from "../operations/OperationDocumentDialogs";
import type { AppRole } from "../types";
import {
  PAYABLE_TRANSITIONS,
  canRunTransition,
  findTransition,
} from "./invoicingRules";
import type { PayableSummary } from "./invoicingTypes";
import { todayIsoDate } from "./payableScreenRules";
import { useInvoicingRun } from "./useInvoicingRun";

const PaidDialog = ({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: (paidOn: string, reference: string) => Promise<string | null>;
}) => {
  const translate = useTranslate();
  const [paidOn, setPaidOn] = useState(todayIsoDate());
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    setBusy(true);
    const message = await onSubmit(paidOn, reference.trim());
    setBusy(false);
    if (message) setError(message);
  };

  return (
    <Dialog open onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {translate("crm.invoicing.payables.paid_title")}
          </DialogTitle>
          <DialogDescription>
            {translate("crm.invoicing.payables.paid_description")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="payable-paid-on">
              {translate("crm.invoicing.payables.paid_on")}
            </Label>
            <Input
              id="payable-paid-on"
              type="date"
              className="h-11 sm:h-9"
              value={paidOn}
              onChange={(event) => setPaidOn(event.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="payable-reference">
              {translate("crm.invoicing.payables.reference")}
            </Label>
            <Input
              id="payable-reference"
              className="h-11 sm:h-9"
              value={reference}
              onChange={(event) => setReference(event.target.value)}
            />
          </div>
        </div>
        {error ? (
          <p role="alert" className="text-sm text-[#B3321C]">
            {error}
          </p>
        ) : null}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            className="h-11 sm:h-9"
            onClick={onClose}
          >
            {translate("crm.operations.documents.cancel")}
          </Button>
          <Button
            type="button"
            variant="gradient"
            className="h-11 sm:h-9"
            disabled={busy || paidOn === ""}
            onClick={confirm}
          >
            {translate("crm.operations.documents.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * The actions of a payable, each shown only to the roles the E6 transition
 * table allows (the RPCs enforce the same table): "Marcar como pagada" (date
 * and reference) and Rechazar back to the previous step (reason required) for
 * contabilidad / direccion. `pendiente_vobo` is resolved on "Pendientes de
 * VoBo"; auditoria, asesora and lider never see an action. The linked
 * receivable status is information only and never gates the payment.
 */
export const PayableActions = ({
  payable,
  role,
}: {
  payable: PayableSummary;
  role: AppRole;
}) => {
  const translate = useTranslate();
  const run = useInvoicingRun();
  const [dialog, setDialog] = useState<"pay" | "reject" | null>(null);
  const close = () => setDialog(null);
  const can = (action: string) =>
    canRunTransition(PAYABLE_TRANSITIONS, action, payable.status, role);
  const rejectTarget = findTransition(
    PAYABLE_TRANSITIONS,
    "reject",
    payable.status,
  )?.to;
  const canReject = payable.status !== "pendiente_vobo" && can("reject");
  // QE6-A6: no Rechazar once paid; later corrections are by adjustment.
  if (
    payable.status === "pagada" &&
    (role === "direccion" || role === "contabilidad")
  ) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="final-note">
        {translate("crm.invoicing.payables.final_note")}
      </p>
    );
  }
  if (!can("pay") && !canReject) return null;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {can("pay") ? (
          <Button
            type="button"
            variant="gradient"
            className="h-11 sm:h-9"
            onClick={() => setDialog("pay")}
          >
            {translate("crm.invoicing.payables.mark_paid")}
          </Button>
        ) : null}
        {canReject && rejectTarget ? (
          <Button
            type="button"
            variant="outline"
            className="h-11 sm:h-9"
            onClick={() => setDialog("reject")}
          >
            {translate("crm.invoicing.payables.reject")}
          </Button>
        ) : null}
      </div>
      {dialog === "pay" ? (
        <PaidDialog
          onClose={close}
          onSubmit={async (paidOn, reference) => {
            const message = await run(
              (provider) =>
                provider.markPayablePaid(
                  payable.id,
                  paidOn,
                  reference || undefined,
                ),
              "crm.invoicing.payables.done.paid",
            );
            if (!message) close();
            return message;
          }}
        />
      ) : null}
      {dialog === "reject" && rejectTarget ? (
        <ReasonDialog
          title={translate("crm.invoicing.payables.reject_title")}
          description={translate("crm.invoicing.payables.reject_to", {
            status: translate(`crm.invoicing.status.payable.${rejectTarget}`),
          })}
          onClose={close}
          onSubmit={async (reason) => {
            const message = await run(
              (provider) => provider.rejectPayable(payable.id, reason),
              "crm.invoicing.payables.done.rejected",
            );
            if (!message) close();
            return message;
          }}
        />
      ) : null}
    </div>
  );
};
